import { RegenerateConfirmDialog } from "@components/shared/RegenerateConfirmDialog";
import { CASCADE_GENERATE_BLUEPRINT_ARTEFACTS } from "@lib/graphql/mutations";
import { CASCADE_ARTEFACTS_QUERY } from "@lib/graphql/queries";
import { useCampaignChannel } from "@lib/hooks/useCampaignChannel";
import { AlertCircle, Check, Loader2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "urql";

export type ArtefactKind =
  | "screening_criteria"
  | "interview_guide"
  | "assessment_guide"
  | "assessment_matrix"
  | "candidate_scoring_matrix";

interface ArtefactDescriptor {
  kind: ArtefactKind;
  label: string;
  description: string;
  enabled: boolean;
}

const ARTEFACTS: ArtefactDescriptor[] = [
  {
    kind: "screening_criteria",
    label: "Screening criteria",
    description: "Must-have, nice-to-have, and exclusion criteria for sourcing.",
    enabled: true,
  },
  {
    kind: "assessment_matrix",
    label: "Assessment matrix",
    description: "Per-dimension criteria for the 4–5, 3, and 1–2 bands.",
    enabled: true,
  },
  {
    kind: "assessment_guide",
    label: "Assessment guide",
    description:
      "Narrative reference for evidence indicators per dimension, derived from the matrix.",
    enabled: true,
  },
  {
    kind: "interview_guide",
    label: "Interview guides",
    description:
      "One guide per interview-flavoured selection step — recruiter calls, interviews, final rounds.",
    enabled: true,
  },
  {
    kind: "candidate_scoring_matrix",
    label: "Candidate scoring matrix",
    description: "Pinned grid of candidates × capability dimensions for inline scoring.",
    enabled: true,
  },
];

interface ArtefactStateRow {
  id: string | null;
  generationState: string | null;
  generationError: string | null;
  // Set of currentVersion ids currently in play for this kind. For most
  // artefacts a single id; for interview_guide the per-step set.
  versionIds: Set<string>;
}

// Per-row effective state. "starting" is the rendered-only state we synth
// when jobs were enqueued but the live data hasn't yet surfaced a new
// generating/draft/error version for this kind.
type EffectiveState = "starting" | "generating" | "draft" | "error" | null;

interface Props {
  open: boolean;
  campaignId: string;
  onClose: () => void;
  onViewDeliverables?: () => void;
  // When provided, only these kinds are pre-checked on open. Used by the
  // relock flow to default the selection to out-of-sync artefacts.
  defaultSelectedKinds?: ArtefactKind[];
}

export function CascadeGenerationModal({
  open,
  campaignId,
  onClose,
  onViewDeliverables,
  defaultSelectedKinds,
}: Props) {
  const [phase, setPhase] = useState<"select" | "progress">("select");
  const initialSelection = useCallback(() => {
    if (defaultSelectedKinds && defaultSelectedKinds.length > 0) {
      return new Set(defaultSelectedKinds);
    }
    return new Set(ARTEFACTS.filter((a) => a.enabled).map((a) => a.kind as ArtefactKind));
  }, [defaultSelectedKinds]);
  const [selected, setSelected] = useState<Set<ArtefactKind>>(initialSelection);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Set when the cascade resolver flags `status: "manual_edits_present"` —
  // some of the requested kinds carry a USER-origin current version that
  // would be overwritten. RegenerateConfirmDialog surfaces the warning;
  // confirming retries the cascade with `confirmOverwrite: true`.
  const [manualEditsConfirm, setManualEditsConfirm] = useState<{
    warning: string;
    affectedKinds: string[];
  } | null>(null);
  // Snapshot of currentVersion ids per kind taken when we enter the
  // progress phase. Until the live data shows a NEW version id (or a
  // "generating" state), the row renders as "Starting…" rather than
  // exposing the user to a stale "Generated" / "Error" pill from the
  // previous draft — which would otherwise flicker for the split second
  // between modal advance and the ActionCable refetch.
  const [priorVersionIds, setPriorVersionIds] = useState<Record<ArtefactKind, Set<string>> | null>(
    null,
  );

  useEffect(() => {
    if (open) {
      setPhase("select");
      setSelected(initialSelection());
      setSubmitError(null);
      setManualEditsConfirm(null);
      setPriorVersionIds(null);
    }
  }, [open, initialSelection]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const [{ data }, refetch] = useQuery({
    query: CASCADE_ARTEFACTS_QUERY,
    variables: { id: campaignId },
    pause: !open,
  });

  const refresh = useCallback(() => refetch({ requestPolicy: "network-only" }), [refetch]);
  useCampaignChannel(open ? campaignId : "", refresh);

  const [{ fetching: enqueuing }, cascadeGenerate] = useMutation(
    CASCADE_GENERATE_BLUEPRINT_ARTEFACTS,
  );

  const stateByKind = useMemo<Record<ArtefactKind, ArtefactStateRow>>(() => {
    const c = data?.campaign;
    return {
      screening_criteria: {
        id: c?.screeningCriteria?.id ?? null,
        generationState: c?.screeningCriteria?.currentVersion?.generationState ?? null,
        generationError: c?.screeningCriteria?.currentVersion?.generationError ?? null,
        versionIds: singleId(c?.screeningCriteria?.currentVersion?.id),
      },
      interview_guide: (() => {
        // Per-step guides (#2252): aggregate them so the modal still has a
        // single row. "generating" wins over "error" wins over "draft".
        const guides: Array<{
          id?: string | null;
          currentVersion?: {
            id?: string | null;
            generationState?: string | null;
            generationError?: string | null;
          } | null;
        }> = c?.interviewGuides ?? [];
        const states = guides
          .map((g) => g.currentVersion?.generationState)
          .filter((s): s is string => Boolean(s));
        const errors = guides
          .map((g) => g.currentVersion?.generationError)
          .filter((e): e is string => Boolean(e));
        const generationState = states.includes("generating")
          ? "generating"
          : states.includes("error")
            ? "error"
            : states.includes("draft")
              ? "draft"
              : null;
        const versionIds = new Set<string>();
        for (const g of guides) {
          if (g.currentVersion?.id) versionIds.add(g.currentVersion.id);
        }
        return {
          id: guides.length > 0 ? (guides[0]?.id ?? null) : null,
          generationState,
          generationError: errors[0] ?? null,
          versionIds,
        };
      })(),
      assessment_guide: {
        id: c?.assessmentGuide?.id ?? null,
        generationState: c?.assessmentGuide?.currentVersion?.generationState ?? null,
        generationError: c?.assessmentGuide?.currentVersion?.generationError ?? null,
        versionIds: singleId(c?.assessmentGuide?.currentVersion?.id),
      },
      assessment_matrix: {
        id: c?.assessmentMatrix?.id ?? null,
        generationState: c?.assessmentMatrix?.currentVersion?.generationState ?? null,
        generationError: c?.assessmentMatrix?.currentVersion?.generationError ?? null,
        versionIds: singleId(c?.assessmentMatrix?.currentVersion?.id),
      },
      candidate_scoring_matrix: {
        id: c?.candidateScoringMatrix?.id ?? null,
        generationState: c?.candidateScoringMatrix?.currentVersion?.generationState ?? null,
        generationError: c?.candidateScoringMatrix?.currentVersion?.generationError ?? null,
        versionIds: singleId(c?.candidateScoringMatrix?.currentVersion?.id),
      },
    };
  }, [data]);

  // Per-row state with the post-submit "starting" pause baked in: while
  // the live data still only shows the snapshot version ids and there's
  // no "generating" yet, we render "Starting…" rather than the stale draft.
  const effectiveStateByKind = useMemo<Record<ArtefactKind, EffectiveState>>(() => {
    const result: Partial<Record<ArtefactKind, EffectiveState>> = {};
    for (const kind of Object.keys(stateByKind) as ArtefactKind[]) {
      result[kind] = deriveEffectiveState(stateByKind[kind], priorVersionIds?.[kind]);
    }
    return result as Record<ArtefactKind, EffectiveState>;
  }, [stateByKind, priorVersionIds]);

  if (!open) return null;

  const toggle = (kind: ArtefactKind) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });
  };

  const runCascade = async (confirmOverwrite: boolean) => {
    const kinds = Array.from(selected);
    if (kinds.length === 0) {
      onClose();
      return;
    }
    setSubmitError(null);
    const result = await cascadeGenerate({ input: { campaignId, kinds, confirmOverwrite } });
    // Four distinct failure / pause modes — all must keep the modal off
    // the progress phase so the user doesn't land on a "Generated" screen
    // where no jobs actually ran:
    //   1. Network/GraphQL exception     → result.error set, data undefined.
    //   2. Resolver error array          → data.cascade.errors populated.
    //   3. Resolver returned nothing     → no data at all.
    //   4. manual_edits_present         → some kinds are USER-origin and
    //      would be overwritten — surface the confirm dialog before any
    //      job is enqueued.
    // The progress phase reads the campaign's CURRENT artefact state, which
    // for any campaign that has ever cascaded successfully will already be
    // "draft" — so silently entering it on failure misleads the user.
    if (result.error) {
      setSubmitError(result.error.message);
      return;
    }
    const payload = result.data?.cascadeGenerateBlueprintArtefacts;
    if (!payload) {
      setSubmitError("Cascade generation did not return a result. Try again.");
      return;
    }
    if (payload.errors && payload.errors.length > 0) {
      setSubmitError(payload.errors.join(", "));
      return;
    }
    if (payload.status === "manual_edits_present" && !confirmOverwrite) {
      setManualEditsConfirm({
        warning: payload.warning ?? "Manual edits will be replaced.",
        affectedKinds: payload.manualEditsKinds ?? [],
      });
      return;
    }
    setManualEditsConfirm(null);
    // Snapshot the version ids visible right now so the progress phase can
    // tell a stale "draft" pill (id unchanged) from a real one (id is new).
    setPriorVersionIds(snapshotVersionIds(data?.campaign));
    setPhase("progress");
  };

  const handleGenerate = () => runCascade(false);
  const handleConfirmOverwrite = () => runCascade(true);
  const handleCancelOverwrite = () => setManualEditsConfirm(null);

  const selectedKinds = Array.from(selected);
  // Settled = no row is still pending. "starting" / "generating" count as
  // unsettled; only "draft" / "error" close the loop.
  const allSettled =
    selectedKinds.length > 0 &&
    selectedKinds.every((k) => {
      const s = effectiveStateByKind[k];
      return s === "draft" || s === "error";
    });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cascade-generation-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <button
        type="button"
        aria-label="Close cascade generation dialog"
        className="absolute inset-0 h-full w-full cursor-default"
        onClick={onClose}
      />
      <div className="relative z-50 w-full max-w-xl rounded-lg bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between">
          <h3 id="cascade-generation-title" className="text-lg font-semibold text-text-primary">
            Generate dependent artefacts
          </h3>
          <button
            type="button"
            aria-label="Close cascade dialog"
            onClick={onClose}
            className="rounded p-1 text-text-faint hover:bg-card-hover"
          >
            <X size={16} />
          </button>
        </div>

        {phase === "select" ? (
          <SelectionPhase
            selected={selected}
            onToggle={toggle}
            onGenerate={handleGenerate}
            onSkip={onClose}
            submitting={enqueuing}
            error={submitError}
          />
        ) : (
          <ProgressPhase
            kinds={selectedKinds}
            stateByKind={stateByKind}
            effectiveStateByKind={effectiveStateByKind}
            allSettled={allSettled}
            onClose={onClose}
            onViewDeliverables={onViewDeliverables}
          />
        )}
      </div>
      <RegenerateConfirmDialog
        open={manualEditsConfirm !== null}
        warning={
          manualEditsConfirm
            ? `${manualEditsConfirm.warning}${
                manualEditsConfirm.affectedKinds.length > 0
                  ? ` Affected: ${manualEditsConfirm.affectedKinds.map(labelFor).join(", ")}.`
                  : ""
              }`
            : ""
        }
        onCancel={handleCancelOverwrite}
        onConfirm={handleConfirmOverwrite}
      />
    </div>
  );
}

function labelFor(kind: string): string {
  return ARTEFACTS.find((a) => a.kind === kind)?.label ?? kind;
}

interface SelectionPhaseProps {
  selected: Set<ArtefactKind>;
  onToggle: (kind: ArtefactKind) => void;
  onGenerate: () => void;
  onSkip: () => void;
  submitting: boolean;
  error: string | null;
}

function SelectionPhase({
  selected,
  onToggle,
  onGenerate,
  onSkip,
  submitting,
  error,
}: SelectionPhaseProps) {
  return (
    <>
      <p className="mt-2 text-sm text-text-muted">
        Now that the blueprint is locked, you can generate the downstream artefacts in one go. Each
        is a draft you can edit afterward.
      </p>
      <ul className="mt-4 space-y-2">
        {ARTEFACTS.map((a) => {
          const isEnabled = a.enabled;
          const checked = isEnabled && selected.has(a.kind as ArtefactKind);
          return (
            <li key={a.kind}>
              <label
                className={`flex items-start gap-3 rounded border border-border p-3 ${
                  isEnabled ? "cursor-pointer hover:bg-card-hover" : "cursor-not-allowed opacity-60"
                }`}
              >
                <input
                  type="checkbox"
                  className="mt-0.5"
                  disabled={!isEnabled}
                  checked={checked}
                  onChange={() => isEnabled && onToggle(a.kind as ArtefactKind)}
                />
                <span>
                  <span className="block text-sm font-medium text-text-primary">{a.label}</span>
                  <span className="mt-0.5 block text-xs text-text-muted">{a.description}</span>
                </span>
              </label>
            </li>
          );
        })}
      </ul>
      {error && <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={onSkip}
          className="rounded px-3 py-1.5 text-sm text-text-secondary hover:bg-card-hover"
        >
          Skip
        </button>
        <button
          type="button"
          onClick={onGenerate}
          disabled={submitting || selected.size === 0}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {submitting ? "Starting…" : "Generate"}
        </button>
      </div>
    </>
  );
}

interface ProgressPhaseProps {
  kinds: ArtefactKind[];
  stateByKind: Record<ArtefactKind, ArtefactStateRow>;
  effectiveStateByKind: Record<ArtefactKind, EffectiveState>;
  allSettled: boolean;
  onClose: () => void;
  onViewDeliverables?: () => void;
}

function ProgressPhase({
  kinds,
  stateByKind,
  effectiveStateByKind,
  allSettled,
  onClose,
  onViewDeliverables,
}: ProgressPhaseProps) {
  return (
    <>
      <p className="mt-2 text-sm text-text-muted">
        Generation runs in the background. You can close this dialog and check back later — drafts
        appear in the Deliverables tab as they finish.
      </p>
      <ul className="mt-4 space-y-2">
        {kinds.map((kind) => {
          const a = ARTEFACTS.find((x) => x.kind === kind);
          if (!a) return null;
          const s = stateByKind[kind];
          const effective = effectiveStateByKind[kind];
          return (
            <li
              key={kind}
              className="flex items-start gap-3 rounded border border-border p-3"
              data-testid={`cascade-row-${kind}`}
            >
              <StateIcon state={effective} />
              <span className="flex-1">
                <span className="block text-sm font-medium text-text-primary">{a.label}</span>
                <StateLabel state={effective} error={s.generationError} />
              </span>
            </li>
          );
        })}
      </ul>
      <div className="mt-4 flex justify-end gap-2">
        {onViewDeliverables && (
          <button
            type="button"
            onClick={onViewDeliverables}
            disabled={!allSettled}
            className="rounded px-3 py-1.5 text-sm text-accent hover:bg-card-hover disabled:cursor-not-allowed disabled:opacity-50"
          >
            View deliverables
          </button>
        )}
        <button
          type="button"
          onClick={onClose}
          disabled={!allSettled}
          className="rounded bg-accent px-3 py-1.5 text-sm font-medium text-white hover:bg-accent/90 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Close
        </button>
      </div>
    </>
  );
}

function StateIcon({ state }: { state: EffectiveState }) {
  if (state === "draft") {
    return <Check size={16} className="mt-0.5 text-emerald-600 dark:text-emerald-400" />;
  }
  if (state === "error") {
    return <AlertCircle size={16} className="mt-0.5 text-rose-600 dark:text-rose-400" />;
  }
  return <Loader2 size={16} className="mt-0.5 animate-spin text-text-muted" />;
}

function StateLabel({ state, error }: { state: EffectiveState; error: string | null }) {
  if (state === "draft") {
    return <span className="block text-xs text-emerald-700 dark:text-emerald-400">Generated</span>;
  }
  if (state === "error") {
    return (
      <span className="block text-xs text-rose-700 dark:text-rose-400">
        Error{error ? ` — ${error}` : ""}
      </span>
    );
  }
  if (state === "starting") {
    return <span className="block text-xs text-text-muted">Starting…</span>;
  }
  return <span className="block text-xs text-text-muted">Generating…</span>;
}

function singleId(id: string | null | undefined): Set<string> {
  return id ? new Set([id]) : new Set();
}

interface CascadeArtefactsCampaign {
  screeningCriteria?: { currentVersion?: { id?: string | null } | null } | null;
  interviewGuides?: Array<{ currentVersion?: { id?: string | null } | null }> | null;
  assessmentGuide?: { currentVersion?: { id?: string | null } | null } | null;
  assessmentMatrix?: { currentVersion?: { id?: string | null } | null } | null;
  candidateScoringMatrix?: { currentVersion?: { id?: string | null } | null } | null;
}

function snapshotVersionIds(
  c: CascadeArtefactsCampaign | null | undefined,
): Record<ArtefactKind, Set<string>> {
  const guides = c?.interviewGuides ?? [];
  const interviewVersionIds = new Set<string>();
  for (const g of guides) {
    if (g.currentVersion?.id) interviewVersionIds.add(g.currentVersion.id);
  }
  return {
    screening_criteria: singleId(c?.screeningCriteria?.currentVersion?.id),
    interview_guide: interviewVersionIds,
    assessment_guide: singleId(c?.assessmentGuide?.currentVersion?.id),
    assessment_matrix: singleId(c?.assessmentMatrix?.currentVersion?.id),
    candidate_scoring_matrix: singleId(c?.candidateScoringMatrix?.currentVersion?.id),
  };
}

// "Starting…" while the live data still matches the pre-submit snapshot
// (no new version landed yet AND nothing's actively generating). The
// instant a new id appears or the live state flips to "generating", we
// trust the live data. This kills the split-second
// "Generated → Generating… → Generated" flicker when re-cascading on a
// campaign that already had drafts.
function deriveEffectiveState(
  row: ArtefactStateRow,
  snapshot: Set<string> | undefined,
): EffectiveState {
  const live = row.generationState as EffectiveState;
  if (!snapshot) return live;
  if (live === "generating") return "generating";
  const hasNewId = [...row.versionIds].some((id) => !snapshot.has(id));
  if (hasNewId) return live;
  return "starting";
}
